import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Mail, Loader2, CheckCircle, XCircle, Lock, Check, X, Shield } from "lucide-react";
import { toast } from "sonner";
import { API_URL } from "../config/api";

const VerifyEmailChange = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('form'); // form, verifying, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const navigate = useNavigate();

  // Password fields
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const isPasswordValid = 
    password.length >= 8 &&
    /[a-z]/.test(password) &&
    /[A-Z]/.test(password) &&
    /[0-9]/.test(password);

  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('No verification token found');
    }
  }, [token]);

  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (!isPasswordValid) {
      toast.error('Please meet all password requirements');
      return;
    }
    
    if (!passwordsMatch) {
      toast.error('Passwords do not match');
      return;
    }

    setStatus('verifying');

    try {
      const response = await axios.post(`${API_URL}/v2/auth/account/verify-email-change`, {
        token,
        newPassword: password
      });

      if (response.data.success) {
        setStatus('success');
        setNewEmail(response.data.newEmail);
        toast.success('Email and password updated successfully!');
      }
    } catch (error) {
      setStatus('error');
      const errorMsg = error.response?.data?.detail || 'Failed to verify email change';
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    }
  };

  // Form state - show password setup
  if (status === 'form') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{background: '#f8f9fa'}}>
        <Card className="w-full max-w-md shadow-xl rounded-xl">
          <CardHeader className="text-center pt-8 pb-4">
            <div className="flex justify-center mb-4">
              <div className="p-3 rounded-xl shadow-lg bg-blue-600">
                <Mail className="w-8 h-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-xl font-bold text-gray-900">
              Verify Email & Set Password
            </CardTitle>
            <p className="text-gray-600 mt-2 text-sm">
              Complete your email change by setting a new password
            </p>
          </CardHeader>

          <CardContent className="px-6 pb-6">
            <form onSubmit={handleVerify} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">New Password</Label>
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a strong password"
                  className="h-11"
                  data-testid="verify-password-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Confirm Password</Label>
                <Input
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="h-11"
                  data-testid="verify-confirm-password-input"
                />
                {confirmPassword && !passwordsMatch && (
                  <p className="text-xs text-red-500">Passwords do not match</p>
                )}
                {passwordsMatch && (
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <Check className="w-3 h-3" /> Passwords match
                  </p>
                )}
              </div>

              {/* Password Requirements */}
              <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
                <p className="text-xs font-semibold text-gray-700 mb-2">Password Requirements:</p>
                <div className="flex items-center gap-2">
                  {password.length >= 8 ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-gray-400" />}
                  <span className={`text-xs ${password.length >= 8 ? 'text-green-700' : 'text-gray-500'}`}>8+ characters</span>
                </div>
                <div className="flex items-center gap-2">
                  {/[a-z]/.test(password) ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-gray-400" />}
                  <span className={`text-xs ${/[a-z]/.test(password) ? 'text-green-700' : 'text-gray-500'}`}>1 lowercase letter</span>
                </div>
                <div className="flex items-center gap-2">
                  {/[A-Z]/.test(password) ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-gray-400" />}
                  <span className={`text-xs ${/[A-Z]/.test(password) ? 'text-green-700' : 'text-gray-500'}`}>1 uppercase letter</span>
                </div>
                <div className="flex items-center gap-2">
                  {/[0-9]/.test(password) ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-gray-400" />}
                  <span className={`text-xs ${/[0-9]/.test(password) ? 'text-green-700' : 'text-gray-500'}`}>1 number</span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="showPassword"
                  checked={showPassword}
                  onChange={() => setShowPassword(!showPassword)}
                  className="rounded border-gray-300"
                />
                <label htmlFor="showPassword" className="text-sm text-gray-600">
                  Show passwords
                </label>
              </div>

              <Button
                type="submit"
                disabled={!isPasswordValid || !passwordsMatch}
                className="w-full h-11 text-base font-semibold bg-blue-600 hover:bg-blue-700"
                data-testid="verify-submit-btn"
              >
                Verify Email & Set Password
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Verifying state
  if (status === 'verifying') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{background: '#f8f9fa'}}>
        <Card className="w-full max-w-md shadow-xl rounded-xl">
          <CardHeader className="text-center py-12">
            <div className="flex justify-center mb-4">
              <div className="p-4 rounded-xl shadow-lg bg-blue-600">
                <Loader2 className="w-10 h-10 text-white animate-spin" />
              </div>
            </div>
            <CardTitle className="text-xl font-bold text-gray-900">
              Verifying...
            </CardTitle>
            <p className="text-gray-600 mt-2">
              Please wait while we update your email and password
            </p>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Success state
  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{background: '#f8f9fa'}}>
        <Card className="w-full max-w-md shadow-xl rounded-xl">
          <CardHeader className="text-center pt-8 pb-4">
            <div className="flex justify-center mb-4">
              <div className="p-4 rounded-xl shadow-lg bg-green-500">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
            </div>
            <CardTitle className="text-xl font-bold text-gray-900">
              All Done!
            </CardTitle>
            <p className="text-gray-600 mt-2">
              Your email and password have been updated
            </p>
          </CardHeader>

          <CardContent className="px-6 pb-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              {newEmail && (
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-green-700">{newEmail}</span>
                </div>
              )}
              <p className="text-sm text-green-800 mt-2">
                ✅ Email verified and password updated
              </p>
            </div>

            <Button
              onClick={() => navigate('/login')}
              className="w-full h-11 text-base font-semibold bg-blue-600 hover:bg-blue-700"
              data-testid="go-to-login-btn"
            >
              Log In Now
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{background: '#f8f9fa'}}>
      <Card className="w-full max-w-md shadow-xl rounded-xl">
        <CardHeader className="text-center pt-8 pb-4">
          <div className="flex justify-center mb-4">
            <div className="p-4 rounded-xl shadow-lg bg-red-500">
              <XCircle className="w-10 h-10 text-white" />
            </div>
          </div>
          <CardTitle className="text-xl font-bold text-gray-900">
            Verification Failed
          </CardTitle>
          <p className="text-gray-600 mt-2">
            {errorMessage || 'Something went wrong'}
          </p>
        </CardHeader>

        <CardContent className="px-6 pb-6">
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/settings')}
              className="w-full h-11 text-base font-semibold bg-blue-600 hover:bg-blue-700"
            >
              Go to Settings
            </Button>
            <Button
              onClick={() => navigate('/login')}
              variant="outline"
              className="w-full h-11 text-base font-semibold"
            >
              Back to Login
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmailChange;
